import { inject, injectable } from 'inversify'
import { makeAutoObservable } from 'mobx'
import { DY_TYPES } from '@compliance/di/serviceIdentifiers'
import { CaseViewModel } from '@compliance/modules/CaseViewModel'
import { normalizeSnakeToCamelCase } from '@caudena/core/utils/normalizeSnakeToCamelCase'

@injectable()
export class CaseViewModelFacade {
  constructor(
    @inject(DY_TYPES.CaseViewModel)
    private viewModel: CaseViewModel
  ) {
    makeAutoObservable(this)
  }

  init = (caseId: number) => {
    return this.viewModel.init(caseId)
  }

  get caseActionsController() {
    return this.viewModel.caseActionsController
  }

  get isInitialized() {
    return this.viewModel.store.isInitialized
  }

  get chatData() {
    return {
      messages: this.viewModel.store.chatDataStore.data.chatMessages,
      status: this.viewModel.store.chatDataStore.data.chatStatus,
      userMessage: this.viewModel.store.chatDataStore.data.chatUserMessage,
      aiResponseMessage:
        this.viewModel.store.chatDataStore.data.chatSystemMessageResponse,
      chatFilesInProcessing:
        this.viewModel.store.chatDataStore.data.filesInProcessing,
      chatMessagesUsedInSummary:
        this.viewModel.store.chatDataStore.data.chatMessagesUsedInSummary,
      error: this.viewModel.store.chatDataStore.data.error,
    }
  }

  get incidentData() {
    return this.viewModel.store.incident
  }

  get caseData() {
    return this.viewModel.store.data
  }

  get aiSummary() {
    return this.viewModel.store.chatDataStore.data.aiSummary
  }

  get aiSummaryStatus() {
    return this.viewModel.store.chatDataStore.data.aiSummaryStatus
  }

  get isSummaryReportProcessing() {
    return this.aiSummaryStatus === 'in_process'
  }

  get isSummaryReportToProcess() {
    return this.aiSummaryStatus === 'to_process'
  }

  get isSummaryReportCantBeModified() {
    return this.isSummaryReportToProcess
  }

  get isSummaryReportCantBeModifiedInChat() {
    return this.isSummaryReportProcessing || this.isSummaryReportCantBeModified
  }

  get isChatDisabled() {
    return this.isSummaryReportCantBeModified
  }

  get clientData() {
    return this.viewModel.store.clientData
  }

  get relatedIncidents() {
    return this.viewModel.store.relatedIncidents
  }

  get rules() {
    return this.viewModel.store.rules
  }

  get isFirstLoading() {
    return this.viewModel.store.isFirstLoading
  }

  get isClientDataFirstLoading() {
    return this.viewModel.store.isClientDataFirstLoading
  }

  get relatedCases() {
    return this.viewModel.store.relatedCases
  }
  get assignees() {
    return this.viewModel.store.assignees
  }
  get activityData() {
    return this.viewModel.store.activityData
  }

  attachFilesToChatMessage = (files: File[]) => {
    return this.viewModel.attachFilesToChatMessage(files)
  }

  removeChatFile = (fileIndex: number) => {
    return this.viewModel.removeChatFile(fileIndex)
  }

  sendAiChatMessage = (message: string) => {
    return this.viewModel.sendAiChatMessage({ message })
  }

  retrySendAiChatMessage = () => {
    return this.viewModel.retrySendAiChatMessage()
  }

  regenerateReport = () => {
    return this.viewModel.regenerateReport()
  }

  useMessageInReport = (messageId: string) => {
    return this.viewModel.useMessageInReport({ messageId })
  }

  clear = () => {
    return this.viewModel.clear()
  }
}
